import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import StatusChip from '../components/shared/StatusChip';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import NewAppointmentDialog from '../components/schedule/NewAppointmentDialog';
import AdvancedScheduleModal from './AdvancedScheduleModal';
import {
  Calendar, CalendarDays, Clock, Plus, RefreshCw, ChevronLeft, ChevronRight,
  User, Users, ClipboardCheck, Stethoscope, Search,
  UserCheck, XCircle, Maximize2, Minimize2, LayoutDashboard, ChevronDown, ChevronUp,
  Coffee, Briefcase, Ban, ArrowRight, FileText, Edit3, AlertTriangle, Palette, Check, Lock
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================
// HELPERS
// ============================================================

function generateTimeSlots(startHour = 8, endHour = 17) {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}

function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function getBlockType(time, providerId, templates, dateStr, overrides) {
  const tmpl = templates[providerId];
  // Check overrides first — unblock overrides everything, block override adds a block
  if (overrides && overrides.length > 0) {
    const hasUnblock = overrides.some(o => o.provider_id === providerId && o.time === time && o.action === 'unblock');
    if (hasUnblock) return { type: 'available', label: '' };
    const blockOverride = overrides.find(o => o.provider_id === providerId && o.time === time && o.action === 'block');
    if (blockOverride) return { type: blockOverride.block_type || 'unavailable', label: blockOverride.block_label || '' };
  }

  if (!tmpl) return { type: 'unavailable', label: 'No Template' };
  let dow;
  if (dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    dow = d.getDay() === 0 ? 7 : d.getDay();
  } else {
    const today = new Date();
    dow = today.getDay() === 0 ? 7 : today.getDay();
  }
  const blocks = tmpl.blocks || [];
  const minutes = timeToMinutes(time);
  for (const block of blocks) {
    if (block.day_of_week !== dow) continue;
    const start = timeToMinutes(block.start_time);
    const end = timeToMinutes(block.end_time);
    if (minutes >= start && minutes < end) {
      return { type: block.block_type, label: block.label || block.block_type };
    }
  }
  const dayBlocks = blocks.filter(b => b.day_of_week === dow);
  if (dayBlocks.length === 0) return { type: 'unavailable', label: '' };
  return { type: 'unavailable', label: '' };
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getDayLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

function isToday(dateStr) {
  return dateStr === new Date().toISOString().split('T')[0];
}

// ============================================================
// MINI CALENDAR
// ============================================================

function MiniCalendar({ selectedDate, onSelect }) {
  const d = new Date(selectedDate + 'T12:00:00');
  const year = d.getFullYear();
  const month = d.getMonth();
  const [viewMonth, setViewMonth] = useState(month);
  const [viewYear, setViewYear] = useState(year);

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="select-none" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-1.5">
        <button className="p-0.5 rounded hover:bg-accent" onClick={() => {
          if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
          else setViewMonth(viewMonth - 1);
        }}><ChevronLeft className="w-3 h-3" /></button>
        <span className="text-[10px] font-semibold">{monthLabel}</span>
        <button className="p-0.5 rounded hover:bg-accent" onClick={() => {
          if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
          else setViewMonth(viewMonth + 1);
        }}><ChevronRight className="w-3 h-3" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[8px] text-muted-foreground font-medium py-0.5">{d}</div>
        ))}
        {days.map((day, i) => {
          if (!day) return <div key={i} />;
          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = dateStr === selectedDate;
          const isTodayDate = dateStr === today;
          return (
            <button key={i} onClick={() => onSelect(dateStr)}
              className={`text-[10px] w-6 h-6 rounded-full flex items-center justify-center transition-colors mx-auto
                ${isSelected ? 'bg-primary text-primary-foreground font-bold' :
                  isTodayDate ? 'bg-primary/10 text-primary font-semibold' :
                  'hover:bg-accent text-foreground'}`}>
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ScheduleBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [templates, setTemplates] = useState({});
  const [visitTypes, setVisitTypes] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [preselectedSlot, setPreselectedSlot] = useState(null);
  const [density, setDensity] = useState('normal'); // 'normal' | 'compact' | 'fullday'
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'week5' | 'week7'
  const [weekDates, setWeekDates] = useState([]);
  const [activeProviderId, setActiveProviderId] = useState(null); // for week views: single provider
  const [visibleProviders, setVisibleProviders] = useState(() => {
    try {
      const saved = localStorage.getItem('hfc_visible_providers');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [calendarOpen, setCalendarOpen] = useState(true);
  const [dragAppt, setDragAppt] = useState(null);
  const [gridInterval, setGridInterval] = useState(15);
  const [scheduleOverrides, setScheduleOverrides] = useState([]);
  const [advancedModal, setAdvancedModal] = useState(null); // {mode, providerId, providerName, date, time}
  const scrollRef = useRef(null);

  // Dedicated override refresh — called after block/unblock operations
  const refreshOverrides = useCallback(async (forDate) => {
    const targetDate = forDate || date;
    if (!targetDate) return;
    try {
      const ovRes = await api.get(`/schedule-overrides?date=${targetDate}`);
      setScheduleOverrides(ovRes.data.overrides || []);
    } catch (e) {
      console.warn('Failed to refresh overrides:', e);
    }
  }, [date]);

  const fetchSchedule = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      params.append('view', viewMode === 'day' ? 'day' : 'week');
      
      const [schedRes, waitRes] = await Promise.all([
        api.get(`/appointments/schedule?${params.toString()}`),
        api.get('/waiting-room'),
      ]);
      setProviders(schedRes.data.providers || []);
      setAppointments(schedRes.data.appointments || []);
      setTemplates(schedRes.data.schedule_templates || {});
      setVisitTypes(schedRes.data.visit_types || []);
      setDate(schedRes.data.date || '');
      setWaitingRoom(waitRes.data.waiting_room || []);
      if (schedRes.data.week_dates) {
        let wd = schedRes.data.week_dates;
        if (viewMode === 'week5') {
          // Filter to Mon-Fri only
          wd = wd.filter(d => { const day = new Date(d + 'T12:00:00').getDay(); return day >= 1 && day <= 5; });
        }
        setWeekDates(wd);
      }
      // Set active provider for week views
      if (viewMode !== 'day' && !activeProviderId && schedRes.data.providers?.length > 0) {
        setActiveProviderId(schedRes.data.providers[0].id);
      }
      setVisibleProviders(prev => {
        if (prev.size === 0 && schedRes.data.providers?.length > 0) {
          return new Set(schedRes.data.providers.map(p => p.id));
        }
        return prev;
      });
      // Fetch grid interval setting
      try {
        const settRes = await api.get('/settings');
        const gi = settRes.data?.settings?.schedule_display?.grid_interval;
        if (gi && [15, 30, 60].includes(gi)) setGridInterval(gi);
      } catch (e) { /* use default */ }
      // Fetch schedule overrides for today
      try {
        const currentDate = schedRes.data.date || '';
        if (currentDate) {
          const ovRes = await api.get(`/schedule-overrides?date=${currentDate}`);
          setScheduleOverrides(ovRes.data.overrides || []);
        }
      } catch (e) { /* no overrides */ }
    } catch (err) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [date, viewMode]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Slot height scales with interval and density mode
  const compact = density === 'compact';
  const isFullDay = density === 'fullday';
  const baseSlotH = isFullDay ? 16 : compact ? 28 : 52;
  const slotH = baseSlotH * (gridInterval / 15);

  // Auto-scroll to current time
  useEffect(() => {
    if (!loading && scrollRef.current && isToday(date)) {
      const now = new Date();
      const currentSlotIdx = Math.max(0, (now.getHours() - 8) * (60 / gridInterval) + Math.floor(now.getMinutes() / gridInterval) - 4);
      scrollRef.current.scrollTop = currentSlotIdx * slotH;
    }
  }, [loading, compact, date, gridInterval, slotH]);

  const markArrived = async (apptId) => {
    try {
      await api.post(`/appointments/${apptId}/transition`, { target_state: 'arrived' });
      toast.success('Patient marked as arrived');
      fetchSchedule();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const cancelAppointment = async (apptId) => {
    try {
      await api.post(`/appointments/${apptId}/transition`, { target_state: 'cancelled', reason: 'Cancelled by staff' });
      toast.success('Appointment cancelled');
      fetchSchedule();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const markNoShow = async (apptId) => {
    try {
      await api.post(`/appointments/${apptId}/transition`, { target_state: 'no_show' });
      toast.success('Marked as no-show');
      fetchSchedule();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const rescheduleAppt = async (apptId, newTime, newProviderId) => {
    try {
      const updates = { time: newTime, reason: 'Rescheduled via drag' };
      if (newProviderId) updates.provider_id = newProviderId;
      await api.put(`/appointments/${apptId}`, updates);
      toast.success('Appointment rescheduled');
      fetchSchedule();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
  };

  const openCheckIn = async (appt) => {
    if (appt.status === 'arrived') {
      try { await api.post(`/appointments/${appt.id}/transition`, { target_state: 'checking_in' }); } catch (e) { /* may already be checking_in */ }
    }
    navigate(`/checkin/${appt.id}`);
  };

  const timeSlots = generateTimeSlots(8, 17);
  const filteredProviders = providers.filter(p => visibleProviders.has(p.id));

  // Filter visible time slots based on grid interval setting
  const visibleTimeSlots = useMemo(() => {
    return timeSlots.filter(t => {
      const m = parseInt(t.split(':')[1]);
      if (gridInterval === 60) return m === 0;
      if (gridInterval === 30) return m === 0 || m === 30;
      return true; // 15-min shows all
    });
  }, [timeSlots, gridInterval]);

  // Visit type color lookup
  const vtColorMap = useMemo(() => {
    const map = {};
    visitTypes.forEach(vt => { map[vt.name] = vt.color; });
    return map;
  }, [visitTypes]);

  // Appointment lookup for the grid
  const getApptForSlot = (providerId, time, forDate) => {
    const targetDate = forDate || date;
    for (const a of appointments) {
      if (a.provider_id !== providerId) continue;
      if (a.date !== targetDate) continue;
      const aStart = timeToMinutes(a.time);
      const aDur = a.duration_minutes || 15;
      const slotMin = timeToMinutes(time);
      if (slotMin === aStart) return { appt: a, isStart: true };
      if (slotMin > aStart && slotMin < aStart + aDur) return { appt: a, isStart: false };
    }
    return null;
  };

  // Current time marker
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const statusBorderColor = (status) => {
    const colors = {
      scheduled: 'border-l-slate-400', arrived: 'border-l-amber-500',
      checking_in: 'border-l-teal-500',
      ready: 'border-l-emerald-500', ready_for_nurse: 'border-l-emerald-500',
      in_triage: 'border-l-orange-500',
      ready_for_provider: 'border-l-blue-500',
      in_visit: 'border-l-violet-500',
      tasks_pending: 'border-l-orange-400',
      ready_for_checkout: 'border-l-emerald-400',
      completed: 'border-l-slate-300',
      cancelled: 'border-l-red-400', no_show: 'border-l-red-500',
    };
    return colors[status] || 'border-l-slate-200';
  };

  const statusDot = (status) => {
    const map = {
      scheduled: { dot: 'bg-slate-400', label: 'Scheduled' },
      arrived: { dot: 'bg-amber-500', label: 'Arrived' },
      checking_in: { dot: 'bg-teal-500', label: 'Checking In' },
      ready: { dot: 'bg-emerald-500', label: 'Ready for Nurse' },
      ready_for_nurse: { dot: 'bg-emerald-500', label: 'Ready for Nurse' },
      in_triage: { dot: 'bg-orange-500', label: 'In Triage' },
      ready_for_provider: { dot: 'bg-blue-500', label: 'Ready for Provider' },
      in_visit: { dot: 'bg-violet-500', label: 'In Visit' },
      tasks_pending: { dot: 'bg-orange-400', label: 'Tasks Pending' },
      ready_for_checkout: { dot: 'bg-emerald-400', label: 'Ready for Checkout' },
      completed: { dot: 'bg-slate-300', label: 'Complete' },
      cancelled: { dot: 'bg-red-400', label: 'Cancelled' },
      no_show: { dot: 'bg-red-500', label: 'No Show' },
    };
    return map[status] || { dot: 'bg-slate-300', label: status };
  };

  if (loading) return (
    <div className="flex h-full">
      <div className="flex-1 p-4 space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-[600px] w-full" />
      </div>
      <div className="w-80 border-l p-3 space-y-2">
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col h-full animate-fadeIn p-1.5 gap-1.5" data-testid="schedule-board">
        {/* Top Bar — floating island */}
        <div className="px-4 py-2 bg-white shrink-0 rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-slate-200/70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Schedule</h1>
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                    const d = new Date(date); d.setDate(d.getDate() - 1);
                    setDate(d.toISOString().split('T')[0]);
                  }}><ChevronLeft className="w-3.5 h-3.5" /></Button>
                  <button className="px-2 py-0.5 text-xs font-medium tabular-nums hover:bg-accent rounded transition-colors" data-testid="schedule-board-date-picker"
                    onClick={() => { const today = new Date().toISOString().split('T')[0]; setDate(today); }}>
                    {isToday(date) ? 'Today' : ''} {getDayLabel(date)}
                  </button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                    const d = new Date(date); d.setDate(d.getDate() + 1);
                    setDate(d.toISOString().split('T')[0]);
                  }}><ChevronRight className="w-3.5 h-3.5" /></Button>
                </div>
                <Badge variant="outline" className="text-[10px] tabular-nums">{appointments.length} appts</Badge>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Providers dropdown — dynamic based on view mode */}
                {(() => {
                  const isWeekMode = viewMode !== 'day';
                  const activeProvider = isWeekMode ? (providers.find(p => p.id === activeProviderId) || providers[0]) : null;
                  const activeIdx = isWeekMode ? providers.findIndex(p => p.id === (activeProvider?.id)) + 1 : 0;
                  return (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-2.5 shadow-sm border-slate-200 hover:shadow-md transition-all" data-testid="provider-filter-button">
                      {isWeekMode ? (
                        <>
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: activeProvider?.color || '#6B7280' }} />
                          <span className="text-[10px] font-semibold truncate max-w-28" style={{ color: activeProvider?.color }}>{activeProvider?.name}</span>
                          <Badge variant="secondary" className="text-[8px] h-3.5 px-1 rounded-full bg-primary/10 text-primary">{activeIdx}/{providers.length}</Badge>
                        </>
                      ) : (
                        <>
                          <Users className="w-3 h-3 text-primary" />
                          <span className="text-[10px] font-semibold">Providers</span>
                          <Badge variant="secondary" className="text-[8px] h-3.5 px-1 rounded-full bg-primary/10 text-primary">{visibleProviders.size}/{providers.length}</Badge>
                        </>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="end">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between px-2 pb-1.5 border-b mb-1">
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          {isWeekMode ? 'Select Provider' : 'Show Providers'}
                        </span>
                        {!isWeekMode && (
                          <button className="text-[10px] text-primary hover:underline"
                            onClick={() => {
                              const allIds = new Set(providers.map(p => p.id));
                              setVisibleProviders(allIds);
                              localStorage.setItem('hfc_visible_providers', JSON.stringify([...allIds]));
                            }}>Show All</button>
                        )}
                      </div>
                      {providers.map(prov => {
                        const isActive = isWeekMode ? prov.id === activeProviderId : visibleProviders.has(prov.id);
                        return (
                          <button key={prov.id}
                            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors text-left ${isActive && isWeekMode ? 'bg-accent' : ''}`}
                            onClick={() => {
                              if (isWeekMode) {
                                setActiveProviderId(prov.id);
                              } else {
                                setVisibleProviders(prev => {
                                  const next = new Set(prev);
                                  if (next.has(prov.id)) { if (next.size > 1) next.delete(prov.id); }
                                  else next.add(prov.id);
                                  localStorage.setItem('hfc_visible_providers', JSON.stringify([...next]));
                                  return next;
                                });
                              }
                            }}
                            data-testid={`provider-filter-${prov.name.replace(/\s/g, '-').toLowerCase()}`}
                          >
                            {isWeekMode ? (
                              <span className={`w-3 h-3 rounded-full border-2 shrink-0 ${isActive ? 'border-primary bg-primary' : 'border-slate-300'}`} />
                            ) : (
                              <Checkbox checked={isActive} className="pointer-events-none" />
                            )}
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: prov.color }} />
                            <span className={`text-xs font-medium flex-1 ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{prov.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                  );
                })()}

                {/* View Mode — cycling icon: 1 Day → 5 Day → 7 Day */}
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 gap-1 px-2 rounded-full" data-testid="schedule-view-toggle"
                    onClick={() => setViewMode(v => v === 'day' ? 'week5' : v === 'week5' ? 'week7' : 'day')}>
                    {viewMode === 'day' ? (
                      <><Calendar className="w-3 h-3" /><span className="text-[9px] font-bold">1</span></>
                    ) : viewMode === 'week5' ? (
                      <><CalendarDays className="w-3 h-3" /><span className="text-[9px] font-bold">5</span></>
                    ) : (
                      <><CalendarDays className="w-3 h-3" /><span className="text-[9px] font-bold">7</span></>
                    )}
                  </Button>
                </TooltipTrigger><TooltipContent className="text-xs">{viewMode === 'day' ? '1 Day → 5 Day' : viewMode === 'week5' ? '5 Day → 7 Day' : '7 Day → 1 Day'}</TooltipContent></Tooltip>

                {/* Density */}
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid="schedule-density-toggle"
                    onClick={() => setDensity(d => d === 'normal' ? 'compact' : d === 'compact' ? 'fullday' : 'normal')}>
                    {density === 'normal'
                      ? <Maximize2 className="w-3.5 h-3.5" />
                      : density === 'compact'
                        ? <Minimize2 className="w-3.5 h-3.5" />
                        : <LayoutDashboard className="w-3.5 h-3.5" />
                    }
                  </Button>
                </TooltipTrigger><TooltipContent className="text-xs">
                  {density === 'normal' ? 'Expanded → Compact' : density === 'compact' ? 'Compact → Full Day' : 'Full Day → Expanded'}
                </TooltipContent></Tooltip>

                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fetchSchedule} data-testid="schedule-board-refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" className="h-7 text-[10px] gap-1" onClick={() => setShowCreateDialog(true)} data-testid="schedule-create-appointment">
                  <Plus className="w-3 h-3" /> New Appointment
                </Button>
                <NewAppointmentDialog
                  open={showCreateDialog}
                  onOpenChange={(open) => { setShowCreateDialog(open); if (!open) setPreselectedSlot(null); }}
                  providers={providers}
                  visitTypes={visitTypes}
                  initialDate={date}
                  initialTime={preselectedSlot?.time}
                  initialProviderId={preselectedSlot?.providerId}
                  onCreated={fetchSchedule}
                  currentUser={user}
                />

                {/* Advanced Block / Unblock Modal */}
                {advancedModal && (
                  <AdvancedScheduleModal
                    mode={advancedModal.mode}
                    providerId={advancedModal.providerId}
                    providerName={advancedModal.providerName}
                    date={advancedModal.date}
                    time={advancedModal.time}
                    onClose={() => setAdvancedModal(null)}
                    onApplied={() => {
                      refreshOverrides(advancedModal.date);
                      fetchSchedule();
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Resizable Layout: Schedule Grid + Right Panel */}
          <ResizablePanelGroup direction="horizontal" className="flex-1">
            {/* Main Grid Panel — elevated card look */}
            <ResizablePanel defaultSize={75} minSize={50}>
              <div className="h-full overflow-auto relative bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.10),0_1px_4px_rgba(0,0,0,0.06)] border border-slate-200 mx-1.5 my-1.5" ref={scrollRef}>
            {viewMode !== 'day' ? (
              /* ============ WEEK VIEW (5-Day or 7-Day) — Single Provider, Data-Rich ============ */
              (() => {
                const activeProvider = providers.find(p => p.id === activeProviderId) || providers[0];
                const activePid = activeProvider?.id;
                const isCompactWeek = density !== 'normal';
                return (
              <div className="min-w-[700px]">
                {/* Compact sticky header: provider tabs + day columns */}
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-100">
                  <div className="flex">
                    {/* Time column header */}
                    <div className="w-14 shrink-0 border-r border-slate-100" />
                    {/* Day headers — compact single line */}
                    {weekDates.map(wd => {
                      const d = new Date(wd + 'T12:00:00');
                      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                      const dayNum = d.getDate();
                      const mon = d.toLocaleDateString('en-US', { month: 'short' });
                      const isT = wd === new Date().toISOString().split('T')[0];
                      const ct = appointments.filter(a => a.date === wd && a.provider_id === activePid).length;
                      return (
                        <div key={wd} className={`flex-1 border-r border-slate-100 px-1 py-1 text-center cursor-pointer hover:bg-accent/20 transition-colors
                          ${isT ? 'bg-primary/5' : ''}`}
                          onClick={() => { setDate(wd); setViewMode('day'); }}>
                          <span className={`text-[10px] font-bold tabular-nums ${isT ? 'text-primary' : 'text-slate-700'}`}>{dayName} {mon} {dayNum}</span>
                          {ct > 0 && <span className="text-[8px] text-slate-400 ml-1">({ct})</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Time Grid — every 15 min labeled */}
                {visibleTimeSlots.map((time) => {
                  const isHour = time.endsWith(':00');
                  const isHalf = time.endsWith(':30');
                  return (
                    <div key={time} className={`flex ${isHour ? 'border-t border-slate-200' : 'border-t border-slate-50'}`}
                      style={{ height: `${isCompactWeek ? Math.max(slotH, 20) : Math.max(slotH, 28)}px` }}>
                      {/* Time label — every 15 min */}
                      <div className="w-14 shrink-0 border-r border-slate-100 px-0.5 flex items-center justify-end pr-1">
                        <span className={`tabular-nums ${isHour ? 'text-[9px] text-slate-500 font-semibold' : isHalf ? 'text-[8px] text-slate-400' : 'text-[7px] text-slate-300'}`}>
                          {formatTime12(time)}
                        </span>
                      </div>
                      {/* Day columns */}
                      {weekDates.map(wd => {
                        const slotAppts = appointments.filter(a => {
                          if (a.date !== wd || a.provider_id !== activePid) return false;
                          return timeToMinutes(a.time) === timeToMinutes(time);
                        });
                        const isT = wd === new Date().toISOString().split('T')[0];
                        return (
                          <div key={wd} className={`flex-1 border-r border-slate-50 px-0.5 ${isT ? 'bg-primary/[0.02]' : ''}`}>
                            {slotAppts.map(appt => {
                              const vtColor = appt.visit_color || vtColorMap[appt.visit_type] || '#6B7280';
                              const isCancelled = ['cancelled', 'no_show'].includes(appt.status);
                              const sDot2 = statusDot(appt.status);
                              const complaint = appt.chief_complaint || appt.nurse_intake?.primary_reason || '';

                              return (
                                <Tooltip key={appt.id}>
                                  <TooltipTrigger asChild>
                                <div
                                  className={`rounded-lg border-l-[3px] ${statusBorderColor(appt.status)} bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] cursor-pointer
                                    hover:shadow-md hover:-translate-y-px transition-all mx-0.5 mb-0.5 ${isCompactWeek ? 'px-1.5 py-0.5' : 'px-2 py-1'} ${isCancelled ? 'opacity-40' : ''}`}
                                  data-testid="week-view-appointment-card">
                                  {isCompactWeek ? (
                                    /* Compact: just name + status dot */
                                    <div className="flex items-center gap-1">
                                      <span className={`w-1 h-1 rounded-full shrink-0 ${sDot2.dot}`} />
                                      <span className="text-[9px] font-semibold text-slate-700 truncate">{appt.patient_name}</span>
                                      <span className="text-[7px] shrink-0 rounded-full px-1" style={{ backgroundColor: vtColor + '15', color: vtColor }}>{appt.visit_type?.split(' ')[0]}</span>
                                    </div>
                                  ) : (
                                    /* Expanded: full data */
                                    <>
                                    <div className="flex items-center gap-1">
                                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sDot2.dot}`} />
                                      <span className="text-[10px] font-bold text-slate-800 truncate flex-1">{appt.patient_name}</span>
                                      <Badge variant="outline" className="text-[7px] h-3.5 px-1 border-0 shrink-0 rounded-full" style={{ backgroundColor: vtColor + '15', color: vtColor }}>{appt.visit_type}</Badge>
                                    </div>
                                    {complaint && <div className="text-[8px] text-slate-400 truncate mt-px">{complaint}</div>}
                                    </>
                                  )}
                                </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="right" align="start" className="p-2 max-w-64">
                                    <p className="text-xs font-bold">{appt.patient_name}</p>
                                    <p className="text-[10px] text-muted-foreground">{formatTime12(appt.time)} · {appt.visit_type} · {sDot2.label}</p>
                                    {complaint && <p className="text-[10px] mt-0.5">{complaint}</p>}
                                    {appt.room && <p className="text-[10px] text-blue-500">Room: {appt.room}</p>}
                                    {appt.checkin?.payer_class && <p className="text-[9px] text-slate-400">{appt.checkin.payer_class}</p>}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
                );
              })()
            ) : (
              /* ============ DAY VIEW ============ */
              <div className="min-w-[500px]">
              {/* Provider Column Headers (sticky) */}
              <div className="flex sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-100">
                <div className="w-14 shrink-0 border-r border-slate-100" />
                {filteredProviders.map(prov => (
                  <div key={prov.id} className="flex-1 min-w-[160px] border-r border-slate-100 px-2 py-2 text-center"
                    style={{ borderTopColor: prov.color, borderTopWidth: '3px' }}>
                    <p className="text-[11px] font-semibold truncate">{prov.name}</p>
                    <p className="text-[9px] text-muted-foreground">{prov.specialty}</p>
                  </div>
                ))}
              </div>

              {/* Current Time Indicator */}
              {isToday(date) && currentMinutes >= 480 && currentMinutes < 1020 && (
                <div className="absolute left-0 right-0 z-20 pointer-events-none"
                  style={{ top: `${((currentMinutes - 480) / gridInterval) * slotH + 40}px` }}>
                  <div className="flex items-center">
                    <div className="w-14 flex justify-end pr-1">
                      <span className="text-[8px] font-bold text-red-500 tabular-nums bg-red-50 px-1 rounded">
                        {formatTime12(`${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`)}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-red-400 relative">
                      <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-red-500" />
                    </div>
                  </div>
                </div>
              )}

              {/* Time Slots */}
              {visibleTimeSlots.map((time) => {
                const isHour = time.endsWith(':00');
                const isHalf = time.endsWith(':30');
                return (
                  <div key={time} className={`flex relative ${
                    isHour ? 'border-t border-slate-200' 
                    : isHalf ? 'border-t border-slate-100' 
                    : 'border-t border-slate-50'
                  }`}
                    style={{ height: `${slotH}px` }}>
                    {/* Time Label */}
                    <div className="w-14 shrink-0 border-r border-slate-100 px-1 flex items-start pt-0.5">
                      {isHour && <span className="text-[9px] text-slate-400 tabular-nums font-medium">{formatTime12(time)}</span>}
                      {isHalf && <span className="text-[8px] text-slate-300 tabular-nums">{formatTime12(time)}</span>}
                    </div>

                    {/* Provider Columns */}
                    {filteredProviders.map(prov => {
                      const block = getBlockType(time, prov.id, templates, date, scheduleOverrides);
                      const slotData = getApptForSlot(prov.id, time);
                      const isSuperUser = user?.is_super_user;

                      // Non-bookable block
                      if (block.type !== 'available') {
                        const blockStyles = {
                          lunch: { bg: 'bg-amber-100/90', stripe: 'repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(245,158,11,0.12) 5px, rgba(245,158,11,0.12) 10px)', icon: Coffee, label: 'Lunch' },
                          admin: { bg: 'bg-sky-100/80', stripe: 'repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(14,165,233,0.1) 5px, rgba(14,165,233,0.1) 10px)', icon: Briefcase, label: 'Admin' },
                          unavailable: { bg: 'bg-slate-200/70', stripe: 'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(100,116,139,0.1) 4px, rgba(100,116,139,0.1) 8px)', icon: Ban, label: '' },
                          meeting: { bg: 'bg-purple-100/80', stripe: 'repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(147,51,234,0.1) 5px, rgba(147,51,234,0.1) 10px)', icon: User, label: 'Meeting' },
                        };
                        const bs = blockStyles[block.type] || blockStyles.unavailable;

                        return isSuperUser ? (
                          <Popover key={prov.id}>
                            <PopoverTrigger asChild>
                              <div className={`flex-1 min-w-[160px] border-r ${bs.bg} flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
                                style={{ backgroundImage: bs.stripe }} data-testid="blocked-slot">
                                {isHour && block.label && (
                                  <span className="flex items-center gap-1 text-[8px] text-slate-500/70 uppercase tracking-wider font-medium">
                                    <bs.icon className="w-2.5 h-2.5" /> {block.label}
                                  </span>
                                )}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-48 p-1" side="right" align="start" data-testid="blocked-slot-popover">
                              <div className="px-2 py-1.5 border-b mb-1">
                                <p className="text-xs font-semibold">{block.label || block.type}</p>
                                <p className="text-[10px] text-muted-foreground">{formatTime12(time)} — {prov.name?.split(' ').pop()}</p>
                              </div>
                              <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors text-emerald-600"
                                onClick={async () => {
                                  try {
                                    await api.post('/schedule-overrides/unblock', { provider_id: prov.id, date, time });
                                    toast.success(`Slot unblocked: ${formatTime12(time)}`);
                                    refreshOverrides(date);
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unblock'); }
                                }} data-testid="unblock-slot-btn">
                                <Check className="w-3.5 h-3.5" /> Unblock This Slot
                              </button>
                              <div className="border-t my-1" />
                              <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors text-emerald-700"
                                onClick={() => setAdvancedModal({ mode: 'unblock', providerId: prov.id, providerName: prov.name, date, time })}
                                data-testid="advanced-unblock-btn">
                                <CalendarDays className="w-3.5 h-3.5" /> Advanced Unblock...
                              </button>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div key={prov.id} className={`flex-1 min-w-[160px] border-r ${bs.bg} flex items-center justify-center`}
                            style={{ backgroundImage: bs.stripe }}>
                            {isHour && block.label && (
                              <span className="flex items-center gap-1 text-[8px] text-slate-500/70 uppercase tracking-wider font-medium">
                                <bs.icon className="w-2.5 h-2.5" /> {block.label}
                              </span>
                            )}
                          </div>
                        );
                      }

                      // Continuation of multi-slot appointment (skip rendering)
                      if (slotData && !slotData.isStart) {
                        return <div key={prov.id} className="flex-1 min-w-[160px] border-r" />;
                      }

                      // Appointment card
                      if (slotData && slotData.isStart) {
                        const appt = slotData.appt;
                        const dur = appt.duration_minutes || 15;
                        const slots = Math.max(1, Math.ceil(dur / gridInterval));
                        const cardH = slots * slotH - 4;
                        const vtColor = appt.visit_color || vtColorMap[appt.visit_type] || '#6B7280';
                        const isCancelled = ['cancelled', 'no_show'].includes(appt.status);
                        const sDot = statusDot(appt.status);
                        const complaint = appt.chief_complaint || appt.visit_type || '';

                        return (
                          <div key={prov.id} className="flex-1 min-w-[160px] border-r border-slate-100 px-0.5 pt-0.5 relative">
                            <Popover>
                              <PopoverTrigger asChild>
                            <div
                              className={`rounded-xl border-l-[4px] ${statusBorderColor(appt.status)} bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] cursor-pointer
                                hover:shadow-md hover:-translate-y-px transition-all group ${isCancelled ? 'opacity-40' : ''} ${dragAppt?.id === appt.id ? 'opacity-50 ring-primary' : ''}`}
                              style={{ height: `${cardH}px` }}
                              draggable={appt.status === 'scheduled'}
                              onDragStart={() => setDragAppt(appt)}
                              onDragEnd={() => setDragAppt(null)}
                              data-testid="schedule-board-appointment-card"
                            >
                              {isFullDay ? (
                                <div className="flex items-center gap-1 px-1 h-full overflow-hidden">
                                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sDot.dot}`} />
                                  <span className="text-[9px] font-medium truncate leading-none">
                                    {appt.patient_name?.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                              ) : compact ? (
                                <div className="flex items-center gap-1.5 px-1.5 h-full overflow-hidden">
                                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sDot.dot}`} />
                                  <span className="text-[10px] font-medium truncate leading-none">{appt.patient_name?.split(' ')[0]}</span>
                                  <span className="text-[9px] text-muted-foreground truncate">{complaint}</span>
                                </div>
                              ) : (
                                <div className="px-2 py-1 overflow-hidden h-full flex flex-col">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-[11px] font-bold truncate flex-1 min-w-0 text-slate-800 leading-tight">{appt.patient_name}</p>
                                    <Badge variant="outline" className="text-[7px] h-3.5 px-1 border-0 shrink-0 rounded-full" style={{ backgroundColor: vtColor + '15', color: vtColor }}>{appt.visit_type}</Badge>
                                  </div>
                                  <div className="flex items-center gap-1 mt-px">
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sDot.dot}`} />
                                    <span className="text-[8px] text-slate-400 font-medium shrink-0">{sDot.label}</span>
                                    {complaint && (
                                      <span className="text-[8px] text-slate-400 truncate ml-1">{complaint}</span>
                                    )}
                                  </div>
                                  {/* Task indicators */}
                                  {appt.nurse_tasks?.length > 0 && (
                                    <div className="flex gap-0.5 mt-px flex-wrap">
                                      {appt.nurse_tasks.filter(t => t.status === 'pending').map(t => (
                                        <span key={t.id} className="text-[7px] font-bold px-1 py-px rounded bg-orange-100 text-orange-700">{t.task_type}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                              </PopoverTrigger>
                              <PopoverContent className="w-52 p-1" side="right" align="start" data-testid="appointment-popover">
                                <div className="px-2 py-1.5 border-b mb-1">
                                  <p className="text-xs font-semibold">{appt.patient_name}</p>
                                  <p className="text-[10px] text-muted-foreground">{formatTime12(appt.time)} - {appt.visit_type}</p>
                                </div>
                                <div className="space-y-0.5">
                                  {appt.status === 'scheduled' && (
                                    <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                      onClick={() => markArrived(appt.id)} data-testid="popover-mark-arrived">
                                      <UserCheck className="w-3.5 h-3.5 text-teal-600" /> Mark Arrived
                                    </button>
                                  )}
                                  {['arrived', 'checking_in'].includes(appt.status) && (
                                    <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                      onClick={() => openCheckIn(appt)} data-testid="popover-checkin">
                                      <ClipboardCheck className="w-3.5 h-3.5 text-primary" /> Continue Check-In
                                    </button>
                                  )}
                                  {appt.status === 'in_visit' && appt.encounter_id && (
                                    <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                      onClick={() => navigate(`/visit/${appt.encounter_id}`)} data-testid="popover-visit">
                                      <Stethoscope className="w-3.5 h-3.5 text-primary" /> Open Visit
                                    </button>
                                  )}
                                  <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                    onClick={() => navigate(`/chart/${appt.patient_id}`)} data-testid="popover-chart">
                                    <FileText className="w-3.5 h-3.5" /> View Chart
                                  </button>
                                  {appt.status === 'scheduled' && (
                                    <>
                                      <div className="border-t my-1" />
                                      <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                        onClick={() => { cancelAppointment(appt.id); }} data-testid="popover-cancel">
                                        <XCircle className="w-3.5 h-3.5 text-red-500" /> Cancel
                                      </button>
                                      <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                                        onClick={async () => {
                                          try {
                                            await api.put(`/appointments/${appt.id}/status`, { status: 'no_show' });
                                            toast.success('Marked as No-Show');
                                            fetchSchedule();
                                          } catch { toast.error('Failed'); }
                                        }} data-testid="popover-noshow">
                                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> No-Show
                                      </button>
                                    </>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        );
                      }

                      // Empty available slot
                      return isSuperUser ? (
                        <Popover key={prov.id}>
                          <PopoverTrigger asChild>
                            <div
                              className={`flex-1 min-w-[160px] border-r cursor-pointer transition-colors group/slot
                                ${dragAppt ? 'hover:bg-primary/10 hover:ring-1 hover:ring-primary/20 hover:ring-inset' : 'hover:bg-primary/[0.02]'}`}
                              onClick={(e) => {
                                // Regular click = new appointment; right-click handled by popover
                                if (e.type === 'click' && !e.ctrlKey) {
                                  setPreselectedSlot({ time, providerId: prov.id });
                                  setShowCreateDialog(true);
                                }
                              }}
                              onDragOver={e => { if (dragAppt) e.preventDefault(); }}
                              onDrop={() => {
                                if (dragAppt && dragAppt.status === 'scheduled') {
                                  rescheduleAppt(dragAppt.id, time, prov.id);
                                  setDragAppt(null);
                                }
                              }}
                              data-testid="available-slot">
                              <div className="w-full h-full flex items-center justify-center opacity-0 group-hover/slot:opacity-100 transition-opacity">
                                {dragAppt ? <ArrowRight className="w-3 h-3 text-primary/40" /> : <Plus className="w-3 h-3 text-muted-foreground/30" />}
                              </div>
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-1" side="right" align="start" data-testid="available-slot-popover">
                            <div className="px-2 py-1.5 border-b mb-1">
                              <p className="text-xs font-semibold text-slate-700">Open Slot</p>
                              <p className="text-[10px] text-muted-foreground">{formatTime12(time)} — {prov.name?.split(' ').pop()}</p>
                            </div>
                            <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors"
                              onClick={() => { setPreselectedSlot({ time, providerId: prov.id }); setShowCreateDialog(true); }}
                              data-testid="new-appt-from-slot-btn">
                              <Plus className="w-3.5 h-3.5 text-primary" /> New Appointment
                            </button>
                            <div className="border-t my-1" />
                            <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors text-rose-600"
                              onClick={async () => {
                                try {
                                  await api.post('/schedule-overrides/range', {
                                    provider_id: prov.id, dates: [date],
                                    start_time: time, end_time: `${String(Math.floor((timeToMinutes(time) + 15) / 60)).padStart(2,'0')}:${String((timeToMinutes(time) + 15) % 60).padStart(2,'0')}`,
                                    action: 'block', block_type: 'unavailable', block_label: 'Blocked',
                                  });
                                  toast.success(`Slot blocked: ${formatTime12(time)}`);
                                  refreshOverrides(date);
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to block'); }
                              }} data-testid="block-slot-btn">
                              <Lock className="w-3.5 h-3.5" /> Block This Slot
                            </button>
                            <button className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-accent transition-colors text-rose-700"
                              onClick={() => setAdvancedModal({ mode: 'block', providerId: prov.id, providerName: prov.name, date, time })}
                              data-testid="advanced-block-btn">
                              <CalendarDays className="w-3.5 h-3.5" /> Advanced Block...
                            </button>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <div key={prov.id}
                          className={`flex-1 min-w-[160px] border-r cursor-pointer transition-colors group/slot
                            ${dragAppt ? 'hover:bg-primary/10 hover:ring-1 hover:ring-primary/20 hover:ring-inset' : 'hover:bg-primary/[0.02]'}`}
                          onClick={() => {
                            setPreselectedSlot({ time, providerId: prov.id });
                            setShowCreateDialog(true);
                          }}
                          onDragOver={e => { if (dragAppt) e.preventDefault(); }}
                          onDrop={() => {
                            if (dragAppt && dragAppt.status === 'scheduled') {
                              rescheduleAppt(dragAppt.id, time, prov.id);
                              setDragAppt(null);
                            }
                          }}>
                          <div className="w-full h-full flex items-center justify-center opacity-0 group-hover/slot:opacity-100 transition-opacity">
                            {dragAppt ? <ArrowRight className="w-3 h-3 text-primary/40" /> : <Plus className="w-3 h-3 text-muted-foreground/30" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            )}
          </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* ============ RIGHT PANEL — Two separate island cards ============ */}
            <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
              <div className="h-full flex flex-col gap-1.5 mx-1.5 my-1.5" data-testid="schedule-right-panel">
          {/* Mini Calendar — island card */}
          <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-slate-200/70 shrink-0">
          <Collapsible open={calendarOpen} onOpenChange={setCalendarOpen}>
            <div className="px-3 pt-3 pb-1">
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Calendar
                </span>
                {calendarOpen ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />}
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="px-3 py-2">
                <MiniCalendar selectedDate={date} onSelect={(d) => setDate(d)} />
              </div>
            </CollapsibleContent>
          </Collapsible>
          </div>

          {/* Waiting Room — island card */}
          <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-slate-200/70 flex-1 flex flex-col min-h-0">
          <div className="px-3 py-2 shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Stethoscope className="w-3 h-3" /> Waiting Room
              </h2>
              <Badge variant="outline" className="text-[9px] tabular-nums h-4 px-1">{waitingRoom.length}</Badge>
            </div>
          </div>

          <ScrollArea className="flex-1" data-testid="waiting-room-panel">
            <div className="p-2 space-y-1">
              {waitingRoom.length === 0 ? (
                <div className="text-center py-6">
                  <Stethoscope className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground/20" />
                  <p className="text-[10px] text-muted-foreground">No patients waiting</p>
                </div>
              ) : (
                ['arrived', 'checking_in', 'ready', 'ready_for_nurse', 'in_triage', 'tasks_pending'].map(status => {
                  const group = waitingRoom.filter(w => w.status === status);
                  if (group.length === 0) return null;
                  const label = {arrived:'Arrived',checking_in:'Checking In',ready:'Ready for Nurse',ready_for_nurse:'Ready for Nurse',in_triage:'In Triage',tasks_pending:'Tasks Pending'}[status] || status;
                  return (
                    <div key={status} className="mb-2">
                      <p className="text-[8px] font-semibold uppercase tracking-wider text-slate-400 px-1 mb-1">
                        {label}
                      </p>
                      {group.map(w => (
                        <div key={w.id} className="rounded-xl border border-slate-100 bg-white p-2.5 mb-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-md hover:-translate-y-px transition-all" data-testid="waiting-room-patient-card">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium truncate">{w.patient_name}</p>
                              <div className="flex items-center gap-1 mt-0.5">
                                <StatusChip status={w.status} size="xs" />
                                <span className="text-[9px] text-muted-foreground tabular-nums">
                                  {w.arrived_at ? new Date(w.arrived_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : w.time}
                                </span>
                              </div>
                            </div>
                            {(w.status === 'arrived' || w.status === 'checking_in') && (
                              <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5 gap-0.5" onClick={() => openCheckIn(w)}>
                                <ClipboardCheck className="w-2.5 h-2.5" /> Check-in
                              </Button>
                            )}
                            {(w.status === 'ready' || w.status === 'ready_for_nurse') && (
                              <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 text-emerald-600 border-emerald-200"
                                onClick={() => navigate(`/nurse-intake/${w.id}`)}>
                                <ClipboardCheck className="w-2.5 h-2.5" /> Intake
                              </Button>
                            )}
                            {w.status === 'in_triage' && (
                              <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 text-orange-600 border-orange-200"
                                onClick={() => navigate(`/nurse-intake/${w.id}`)}>
                                <ClipboardCheck className="w-2.5 h-2.5" /> Continue
                              </Button>
                            )}
                            {w.status === 'tasks_pending' && (
                              <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 text-orange-600 border-orange-200"
                                onClick={() => navigate(`/nurse-intake/${w.id}`)}>
                                <ClipboardCheck className="w-2.5 h-2.5" /> Tasks
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
          </div>
        </div>
            </ResizablePanel>
          </ResizablePanelGroup>
      </div>
    </TooltipProvider>
  );
}
